import { Injectable, OnDestroy } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { registerLocaleData } from '@angular/common';
import { Router } from '@angular/router';
import moment from 'moment';
import { Observable, ReplaySubject, Subscription, map } from 'rxjs';
import { DEFAULT_LOCALE, locales, SUPPORTED_LOCALES } from '../../locales';
import { unsubscribe } from 'src/app/utils/helpers';

@Injectable({ providedIn: 'root' })
export class TranslationHelperService implements OnDestroy {
    subscriptions: Subscription[] = [];
    readonly LANGUAGES_REGEX: RegExp;
    readonly locales = SUPPORTED_LOCALES;
    readonly languages: string[];

    private configured = false;
    private currentLocale = DEFAULT_LOCALE;
    private currentTranslations: Record<string, unknown> = {};

    fallbackLocale: string = DEFAULT_LOCALE;

    protected localeSubject = new ReplaySubject<string>(1);

    constructor(private http: HttpClient, protected router: Router) {
        unsubscribe(this.subscriptions);
        this.languages = this.locales.map((locale) => locale.split('-')[0]);
        this.LANGUAGES_REGEX = new RegExp('^\/' + this.languages.map((lang) => `(${lang})`).join('|'), 'i');
        this.subscriptions.push(
            this.getLocale().subscribe((locale) => {
                this.currentLocale = locale;
            })
        );
    }

    init() {
        if (this.configured) {
            throw new Error('Already initialized');
        }

        this.configured = true;
        this.changeLocale(this.getDefaultLocale(), true);
    }

    public onRouteEvent(language: string) {
        if (language) {
            let locale = language + '-BE';
            if (locale !== this.currentLocale) {
                this.changeLocale(locale, false);
            }
        } else {
            let defaultLocale = this.getDefaultLocale();
            this.changeLocale(defaultLocale, true);
        }
    }

    changeLocale(locale: string, forceDomainOverride: boolean = false): void {
        if (forceDomainOverride) {
            const domainLocale = this.getDomainBasedLocale();
            if (domainLocale) {
                locale = domainLocale;
            }
        }

        if (locales[locale] === undefined) {
            locale = this.fallbackLocale;
        }

        const language = locale.split('-')[0];
        moment.locale(language);
        registerLocaleData(locales[locale].data);

        this.http.get<Record<string, unknown>>(`assets/i18n/${locale}.json`).subscribe({
            next: (translations) => {
                this.currentTranslations = translations ?? {};
                this.localeSubject.next(locale);
            },
            error: () => {
                this.currentTranslations = {};
                this.localeSubject.next(locale);
            },
        });
    }

    getLocale(): Observable<string> {
        return this.localeSubject.asObservable();
    }

    getLanguage(): Observable<string> {
        return this.localeSubject.asObservable().pipe(map((locale) => locale.split('-')[0]));
    }

    getDefaultLanguage() {
        return this.getDefaultLocale().split('-')[0];
    }

    public getDomainBasedLocale(): string | null {
        try {
            const hostname = typeof window !== 'undefined' && window.location && window.location.hostname ? window.location.hostname : '';

            if (hostname.includes('ibpt-data.be')) {
                return 'fr-BE';
            }
            if (hostname.includes('bipt-data.be')) {
                return 'nl-BE';
            }

            return null;
        } catch (error) {
            return null;
        }
    }

    getDefaultLocale() {
        let locale = (typeof navigator !== 'undefined' && navigator && (navigator as any).language) || '';

        if (locales[locale] === undefined) {
            locale = locale.split('-')[0].toLocaleLowerCase() + '-BE';
            if (locales[locale] === undefined) {
                locale = this.fallbackLocale;
            }
        }
        return locale;
    }

    get(key: string): Observable<string> {
        return this.localeSubject.asObservable().pipe(map(() => this.resolveTranslation(key)));
    }

    getAll(...keys: Array<string>): Observable<Array<string>> {
        return this.localeSubject.asObservable().pipe(map(() => keys.map((key) => this.resolveTranslation(key))));
    }

    getAllLabels(...keys: Array<string>): Observable<Array<string>> {
        const prefix = 'labels.';
        return this.localeSubject.asObservable().pipe(
            map(() =>
                keys.map((key) => {
                    const translation = this.resolveTranslation(prefix + key);
                    return translation.startsWith(prefix) ? translation.substr(prefix.length) : translation;
                })
            )
        );
    }

    observe(key: string): Observable<string> {
        return this.get(key);
    }

    observeAll(...keys: Array<string>): Observable<Array<string>> {
        return this.getAll(...keys);
    }

    observeAllLabels(...keys: Array<string>): Observable<Array<string>> {
        return this.getAllLabels(...keys);
    }

    getLocalizedMoment(): Observable<moment.Moment> {
        return this.getLocale().pipe(
            map((locale) => {
                let localizedMoment = moment();
                localizedMoment.locale(locale);

                return localizedMoment;
            })
        );
    }

    ngOnDestroy(): void {
        unsubscribe(this.subscriptions);
    }

    private resolveTranslation(key: string): string {
        const translation = this.lookupValue(this.currentTranslations, key);
        if (translation === undefined || translation === null) {
            return key;
        }

        if (typeof translation === 'string') {
            return translation;
        }

        if (typeof translation === 'number' || typeof translation === 'boolean') {
            return String(translation);
        }

        return key;
    }

    private lookupValue(source: unknown, key: string): unknown {
        return key.split('.').reduce<unknown>((current, part) => {
            if (current === null || current === undefined || typeof current !== 'object') {
                return undefined;
            }

            return (current as Record<string, unknown>)[part];
        }, source);
    }
}
